import React, { useCallback, useState, useEffect } from 'react'
import { Typography, Checkbox, makeStyles } from '@material-ui/core'
import { gray } from '../../../utils/colors'
import { FONT_WEIGHTS } from '../../../utils'

const useStyles = makeStyles(() => ({
	root: {
		display: 'flex',
		flexDirection: 'column'
	},
	container: {
		border: '1px solid',
		borderColor: gray[500],
		borderRadius: '6px',
		'& > div': {
			borderBottom: '1px solid',
			borderBottomColor: gray[500],
			display: 'flex',
			alignItems: 'center',
			padding: '0.6rem 0'
		},
		'& > div:last-child': {
			borderBottom: 'none'
		},
		'& > div[disabled]': {
			opacity: 0.5
		}
	},
	label: {
		fontWeight: FONT_WEIGHTS.SemiBold,
		fontFamily: 'Nunito',
		textTransform: 'capitalize',
		display: 'flex',
		flexDirection: 'column',
		'& > span': {
			fontSize: '.8rem',
			fontWeight: FONT_WEIGHTS.Normal,
			color: gray[800]
		}
	},
	header: {
		fontFamily: 'Nunito',
		fontSize: '0.75rem',
		color: gray[900],
		textTransform: 'uppercase',
		fontWeight: FONT_WEIGHTS.ExtraBold,
		letterSpacing: '0.0625rem',
		marginBottom: '0.6rem'
	},
	subheader: {
		fontFamily: 'Nunito',
		fontSize: '0.875rem',
		color: gray[900],
		fontWeight: FONT_WEIGHTS.SemiBold,
		marginBottom: '0.6rem'
	}
}))

const CheckboxMenu = ({
	options = [],
	header,
	subheader,
	selectedOptions = [],
	onSelectedChanged,
	disabledText = '(Not Available)',
	singleSelect = false,
	maxSelections = Infinity
}) => {
	const classes = useStyles()
	const [editableOptions, setEditableOptions] = useState(selectedOptions)
	const selectedOptionsKey = (selectedOptions || []).join('\0')

	useEffect(() => {
		setEditableOptions(selectedOptionsKey === '' ? [] : selectedOptionsKey.split('\0'))
	}, [selectedOptionsKey])

	const handleSelection = useCallback(
		(e) => {
			const { checked, value } = e.target

			setEditableOptions((prev) => {
				let newValues = [...prev]
				if (singleSelect || (maxSelections === 1 && checked)) {
					newValues = [value]
				} else {
					if (checked) {
						newValues.push(value)
					} else {
						newValues = newValues.filter((v) => v !== value)
					}
				}

				while (newValues.length > maxSelections) {
					newValues.shift()
				}

				if (onSelectedChanged) {
					onSelectedChanged(newValues)
				}
				return newValues
			})
		},
		[setEditableOptions, onSelectedChanged]
	)

	return options.length === 0 ? null : (
		<div className={classes.root}>
			{header ? (
				<Typography variant="h6" className={classes.header}>
					{header}
				</Typography>
			) : null}
			{subheader ? (
				<Typography variant="body1" className={classes.subheader}>
					{subheader}
				</Typography>
			) : null}
			<div className={classes.container}>
				{options.map((option, index) => (
					<div disabled={option.disabled} key={`checkbox-menu-item-${index}`}>
						<Checkbox
							value={option.value}
							checked={editableOptions.includes(option.value)}
							disabled={option.disabled}
							className={classes.checkbox}
							onChange={handleSelection}
							style={{
								marginLeft: '8px',
								marginRight: '8px'
							}}
						/>
						{option.label ? (
							<Typography className={classes.label}>
								{option.label}
								{option.disabled ? ` ${disabledText}` : ''}
							</Typography>
						) : null}
						{option.component ? option.component : null}
					</div>
				))}
			</div>
		</div>
	)
}

export default CheckboxMenu
